import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import RichTextEditor from "@/components/editor/RichTextEditor";
import {
  ArrowLeft,
  Save,
  Send,
  Calendar,
  Sparkles,
  Eye,
  Loader2,
  Check,
  X,
  Image,
  MapPin,
  Users,
  Wand2,
} from "lucide-react";
import toast from "react-hot-toast";

interface Trip {
  id: string;
  title: string;
  destination?: {
    name: string;
  };
  priceFrom: number;
  durationDays: number;
  featuredImage?: string;
}

interface Campaign {
  id: string;
  subject: string;
  previewText?: string;
  content?: string;
  tripIds: string[];
  status: "draft" | "scheduled" | "sent";
  scheduledFor?: string;
  aiGenerated?: boolean;
  createdAt: string;
}

interface GeneratedContent {
  subject: string;
  previewText: string;
  content: string;
}

interface Segment {
  id: string;
  name: string;
  description?: string;
  aiPrompt: string;
  customerCount: number;
  lastGeneratedAt?: string;
}

export default function CampaignEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isNew = id === "new";

  // Form state
  const [subject, setSubject] = useState("");
  const [previewText, setPreviewText] = useState("");
  const [content, setContent] = useState("");
  const [selectedTripIds, setSelectedTripIds] = useState<string[]>([]);
  const [selectedSegmentId, setSelectedSegmentId] = useState<string | null>(null);
  const [scheduledFor, setScheduledFor] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [showAiOptions, setShowAiOptions] = useState(false);
  const [showSegmentSelector, setShowSegmentSelector] = useState(false);
  const [aiTone, setAiTone] = useState<"friendly" | "formal" | "excited">("friendly");
  const [aiTheme, setAiTheme] = useState("");

  // Fetch campaign if editing
  const { data: campaignData, isLoading: loadingCampaign } = useQuery({
    queryKey: ["newsletter-campaign", id],
    queryFn: () => api.get<ApiResponse<Campaign>>(`/api/admin/newsletter/campaigns/${id}`),
    enabled: !isNew && !!id,
  });

  // Fetch trips for selection
  const { data: tripsData } = useQuery({
    queryKey: ["trips-for-newsletter"],
    queryFn: () => api.get<ApiResponse<Trip[]>>("/api/admin/trips?status=active"),
  });

  // Fetch segments
  const { data: segmentsData } = useQuery({
    queryKey: ["segments"],
    queryFn: () => api.get<ApiResponse<Segment[]>>("/api/admin/segments"),
  });

  const trips = tripsData?.data || [];
  const segments = segmentsData?.data || [];
  const campaign = campaignData?.data;
  const selectedSegment = segments.find((s) => s.id === selectedSegmentId);

  // Populate form when campaign loads
  useEffect(() => {
    if (campaign) {
      setSubject(campaign.subject || "");
      setPreviewText(campaign.previewText || "");
      setContent(campaign.content || "");
      setSelectedTripIds(campaign.tripIds || []);
      setScheduledFor(campaign.scheduledFor || "");
    }
  }, [campaign]);

  // Create campaign mutation
  const createMutation = useMutation({
    mutationFn: (data: Partial<Campaign>) =>
      api.post<ApiResponse<Campaign>>("/api/admin/newsletter/campaigns", data),
    onSuccess: (response) => {
      toast.success("Kampaň vytvořena");
      queryClient.invalidateQueries({ queryKey: ["newsletter-campaigns"] });
      navigate(`/newsletter/editor/${response.data.id}`);
    },
    onError: () => {
      toast.error("Nepodařilo se vytvořit kampaň");
    },
  });

  // Update campaign mutation
  const updateMutation = useMutation({
    mutationFn: (data: Partial<Campaign>) =>
      api.put<ApiResponse<Campaign>>(`/api/admin/newsletter/campaigns/${id}`, data),
    onSuccess: () => {
      toast.success("Kampaň uložena");
      queryClient.invalidateQueries({ queryKey: ["newsletter-campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["newsletter-campaign", id] });
    },
    onError: () => {
      toast.error("Nepodařilo se uložit kampaň");
    },
  });

  // AI generation mutation
  const generateMutation = useMutation({
    mutationFn: async () => {
      if (isNew) {
        // First create the campaign, then generate
        const createResponse = await api.post<ApiResponse<Campaign>>(
          "/api/admin/newsletter/campaigns",
          { subject: "Nová kampaň", tripIds: selectedTripIds }
        );
        const newId = createResponse.data?.id;

        if (!newId) {
          throw new Error("Nepodařilo se vytvořit kampaň - chybí ID");
        }

        const generateResponse = await api.post<
          ApiResponse<{ data: Campaign; generated: GeneratedContent }>
        >(`/api/admin/newsletter/campaigns/${newId}/generate`, {
          tripIds: selectedTripIds,
          tone: aiTone,
          theme: aiTheme || undefined,
        });
        return { ...generateResponse, campaignId: newId };
      } else {
        if (!id) {
          throw new Error("Chybí ID kampaně");
        }
        const response = await api.post<
          ApiResponse<{ data: Campaign; generated: GeneratedContent }>
        >(`/api/admin/newsletter/campaigns/${id}/generate`, {
          tripIds: selectedTripIds,
          tone: aiTone,
          theme: aiTheme || undefined,
        });
        return { ...response, campaignId: id };
      }
    },
    onSuccess: (response: { generated?: GeneratedContent; campaignId?: string }) => {
      const generated = response.generated;
      if (generated) {
        setSubject(generated.subject);
        setPreviewText(generated.previewText);
        setContent(generated.content);
      }
      setShowAiOptions(false);
      toast.success("Obsah vygenerován pomocí AI");
      queryClient.invalidateQueries({ queryKey: ["newsletter-campaigns"] });
      if (isNew && response.campaignId) {
        navigate(`/newsletter/editor/${response.campaignId}`, { replace: true });
      }
    },
    onError: (error: any) => {
      const message = error?.message || "Nepodařilo se vygenerovat obsah";
      toast.error(message);
    },
  });

  // Send/schedule mutation
  const sendMutation = useMutation({
    mutationFn: (data: { scheduledFor?: string }) =>
      api.post<ApiResponse<{ message: string; recipientCount: number }>>(
        `/api/admin/newsletter/campaigns/${id}/send`,
        data
      ),
    onSuccess: (response) => {
      toast.success(response.data?.message || "Kampaň odeslána");
      queryClient.invalidateQueries({ queryKey: ["newsletter-campaigns"] });
      navigate("/newsletter");
    },
    onError: () => {
      toast.error("Nepodařilo se odeslat kampaň");
    },
  });

  const handleSave = async () => {
    const data = {
      subject,
      previewText,
      content,
      tripIds: selectedTripIds,
      scheduledFor: scheduledFor || undefined,
    };

    if (isNew) {
      createMutation.mutate(data);
    } else {
      updateMutation.mutate(data);
    }
  };

  const handleSend = (scheduled?: string) => {
    if (!id || isNew) {
      toast.error("Nejdřív uložte kampaň");
      return;
    }
    sendMutation.mutate({ scheduledFor: scheduled });
  };

  const toggleTrip = (tripId: string) => {
    setSelectedTripIds((prev) =>
      prev.includes(tripId)
        ? prev.filter((id) => id !== tripId)
        : [...prev, tripId]
    );
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const isSent = campaign?.status === "sent";

  if (!isNew && loadingCampaign) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/newsletter")}
            className="btn-ghost btn-sm"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {isNew ? "Nová kampaň" : "Upravit kampaň"}
            </h1>
            {campaign?.status && (
              <span
                className={`mt-1 inline-block text-sm ${
                  campaign.status === "sent"
                    ? "text-green-600"
                    : campaign.status === "scheduled"
                      ? "text-yellow-600"
                      : "text-gray-500"
                }`}
              >
                {campaign.status === "sent"
                  ? "Odesláno"
                  : campaign.status === "scheduled"
                    ? "Naplánováno"
                    : "Koncept"}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="btn-secondary"
          >
            <Eye className="h-5 w-5" />
            Náhled
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || isSent}
            className="btn-primary"
          >
            {isSaving ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Save className="h-5 w-5" />
            )}
            Uložit
          </button>
          {!isNew && !isSent && (
            <button
              onClick={() => handleSend()}
              disabled={sendMutation.isPending}
              className="btn-primary bg-green-600 hover:bg-green-700"
            >
              {sendMutation.isPending ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Send className="h-5 w-5" />
              )}
              Odeslat
            </button>
          )}
        </div>
      </div>

      {isSent && (
        <div className="rounded-lg bg-yellow-50 p-4 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-200">
          Tato kampaň již byla odeslána a nelze ji upravovat.
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main content */}
        <div className="space-y-6 lg:col-span-2">
          {/* Subject & Preview */}
          <div className="card space-y-4">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Předmět emailu
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Nové zájezdy do exotických destinací"
                className="input"
                disabled={isSent}
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Náhledový text
              </label>
              <input
                type="text"
                value={previewText}
                onChange={(e) => setPreviewText(e.target.value)}
                placeholder="Krátký text zobrazený v náhledu emailu..."
                className="input"
                maxLength={150}
                disabled={isSent}
              />
              <p className="mt-1 text-xs text-gray-500">
                {previewText.length}/150 znaků
              </p>
            </div>
          </div>

          {/* AI Generation */}
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-gray-900 dark:text-white">
                  AI generování obsahu
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Vyberte zájezdy a nechte AI vygenerovat newsletter
                </p>
              </div>
              <button
                onClick={() => setShowAiOptions(!showAiOptions)}
                disabled={selectedTripIds.length === 0 || generateMutation.isPending || isSent}
                className="btn-secondary"
              >
                {generateMutation.isPending ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Sparkles className="h-5 w-5" />
                )}
                Generovat AI
              </button>
            </div>

            {showAiOptions && (
              <div className="mt-4 space-y-4 rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Tón komunikace
                  </label>
                  <div className="flex gap-2">
                    {[
                      { value: "friendly", label: "Přátelský" },
                      { value: "formal", label: "Formální" },
                      { value: "excited", label: "Nadšený" },
                    ].map((option) => (
                      <button
                        key={option.value}
                        onClick={() =>
                          setAiTone(option.value as typeof aiTone)
                        }
                        className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                          aiTone === option.value
                            ? "bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400"
                            : "bg-white text-gray-600 hover:bg-gray-100 dark:bg-gray-700 dark:text-gray-300"
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Téma (volitelné)
                  </label>
                  <input
                    type="text"
                    value={aiTheme}
                    onChange={(e) => setAiTheme(e.target.value)}
                    placeholder="např. Letní dovolená, Dobrodružství, Romantika..."
                    className="input"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setShowAiOptions(false)}
                    className="btn-ghost"
                  >
                    Zrušit
                  </button>
                  <button
                    onClick={() => generateMutation.mutate()}
                    disabled={generateMutation.isPending}
                    className="btn-primary"
                  >
                    {generateMutation.isPending ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Generuji...
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-5 w-5" />
                        Vygenerovat obsah
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Content Editor */}
          <div className="card">
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Obsah emailu
            </label>
            {isSent ? (
              <div
                className="prose prose-sm dark:prose-invert max-w-none rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800"
                dangerouslySetInnerHTML={{ __html: content }}
              />
            ) : (
              <RichTextEditor
                content={content}
                onChange={setContent}
                placeholder="Obsah vašeho newsletteru..."
              />
            )}
          </div>

          {/* Schedule */}
          {!isSent && !isNew && (
            <div className="card">
              <h3 className="mb-4 font-medium text-gray-900 dark:text-white">
                Naplánovat odeslání
              </h3>
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <input
                    type="datetime-local"
                    value={scheduledFor}
                    onChange={(e) => setScheduledFor(e.target.value)}
                    className="input"
                    min={new Date().toISOString().slice(0, 16)}
                  />
                </div>
                <button
                  onClick={() => handleSend(scheduledFor)}
                  disabled={!scheduledFor || sendMutation.isPending}
                  className="btn-secondary"
                >
                  <Calendar className="h-5 w-5" />
                  Naplánovat
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Segment Selection */}
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium text-gray-900 dark:text-white">
                Cílová skupina
              </h3>
              {!isSent && (
                <button
                  onClick={() => setShowSegmentSelector(!showSegmentSelector)}
                  className="btn-ghost btn-sm"
                >
                  <Wand2 className="h-4 w-4" />
                  {selectedSegment ? "Změnit" : "Vybrat segment"}
                </button>
              )}
            </div>

            {selectedSegment ? (
              <div className="rounded-lg border border-primary-200 bg-primary-50 p-3 dark:border-primary-800 dark:bg-primary-900/20">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-primary-900 dark:text-primary-100">
                      {selectedSegment.name}
                    </p>
                    <p className="text-sm text-primary-700 dark:text-primary-300">
                      {selectedSegment.customerCount} zákazníků
                    </p>
                  </div>
                  {!isSent && (
                    <button
                      onClick={() => setSelectedSegmentId(null)}
                      className="text-primary-600 hover:text-primary-800"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <p className="mt-2 text-xs text-primary-600 dark:text-primary-400">
                  {selectedSegment.aiPrompt}
                </p>
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-gray-300 p-4 text-center dark:border-gray-600">
                <Users className="mx-auto h-8 w-8 text-gray-400" />
                <p className="mt-2 text-sm text-gray-500">
                  Všichni odběratelé newsletteru
                </p>
                <button
                  onClick={() => setShowSegmentSelector(true)}
                  className="mt-2 text-sm text-primary-600 hover:underline"
                >
                  Nebo vyberte AI segment
                </button>
              </div>
            )}

            {/* Segment Selector Dropdown */}
            {showSegmentSelector && (
              <div className="mt-4 rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
                <p className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Vyberte segment:
                </p>
                <div className="max-h-48 space-y-2 overflow-y-auto">
                  {segments.length === 0 ? (
                    <p className="py-4 text-center text-sm text-gray-500">
                      Žádné segmenty. <a href="/segments" className="text-primary-600 hover:underline">Vytvořte nový</a>
                    </p>
                  ) : (
                    segments.map((segment) => (
                      <button
                        key={segment.id}
                        onClick={() => {
                          setSelectedSegmentId(segment.id);
                          setShowSegmentSelector(false);
                        }}
                        className={`flex w-full items-center justify-between rounded-lg border p-2 text-left transition-colors ${
                          selectedSegmentId === segment.id
                            ? "border-primary-500 bg-primary-50 dark:bg-primary-900/20"
                            : "border-gray-200 hover:border-gray-300 dark:border-gray-700"
                        }`}
                      >
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {segment.name}
                          </p>
                          <p className="text-xs text-gray-500">{segment.aiPrompt}</p>
                        </div>
                        <span className="text-sm text-gray-500">
                          {segment.customerCount}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Trip Selection */}
          <div className="card">
            <h3 className="mb-4 font-medium text-gray-900 dark:text-white">
              Zájezdy v newsletteru ({selectedTripIds.length})
            </h3>
            <div className="max-h-[500px] space-y-2 overflow-y-auto">
              {trips.map((trip) => {
                const isSelected = selectedTripIds.includes(trip.id);
                return (
                  <button
                    key={trip.id}
                    onClick={() => toggleTrip(trip.id)}
                    disabled={isSent}
                    className={`flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors ${
                      isSelected
                        ? "border-primary-500 bg-primary-50 dark:border-primary-600 dark:bg-primary-900/20"
                        : "border-gray-200 bg-white hover:border-gray-300 dark:border-gray-700 dark:bg-gray-800"
                    } ${isSent ? "cursor-not-allowed opacity-60" : ""}`}
                  >
                    <div className="relative h-12 w-12 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100">
                      {trip.featuredImage ? (
                        <img
                          src={trip.featuredImage}
                          alt={trip.title}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center">
                          <Image className="h-5 w-5 text-gray-400" />
                        </div>
                      )}
                      {isSelected && (
                        <div className="absolute inset-0 flex items-center justify-center bg-primary-500/80">
                          <Check className="h-5 w-5 text-white" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 overflow-hidden">
                      <p className="truncate font-medium text-gray-900 dark:text-white">
                        {trip.title}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <MapPin className="h-3 w-3" />
                        {trip.destination?.name || "—"}
                        <span>•</span>
                        {trip.durationDays} dní
                        <span>•</span>
                        {trip.priceFrom?.toLocaleString("cs-CZ")} Kč
                      </div>
                    </div>
                  </button>
                );
              })}
              {trips.length === 0 && (
                <p className="py-8 text-center text-gray-500">
                  Žádné aktivní zájezdy
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="relative max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 dark:bg-gray-900">
            <button
              onClick={() => setShowPreview(false)}
              className="absolute right-4 top-4 rounded-full p-2 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <X className="h-5 w-5" />
            </button>
            <h2 className="mb-4 text-xl font-bold text-gray-900 dark:text-white">
              Náhled emailu
            </h2>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
              <div className="mb-4 border-b border-gray-200 pb-4 dark:border-gray-700">
                <p className="text-sm text-gray-500">Předmět:</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {subject || "(bez předmětu)"}
                </p>
                {previewText && (
                  <>
                    <p className="mt-2 text-sm text-gray-500">Náhled:</p>
                    <p className="text-gray-600 dark:text-gray-400">
                      {previewText}
                    </p>
                  </>
                )}
              </div>
              <div
                className="prose prose-sm dark:prose-invert max-w-none"
                dangerouslySetInnerHTML={{
                  __html: content || "<p>Prázdný obsah...</p>",
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
